import { useMutation, useQuery } from '@apollo/react-hooks';
import { Table, Button, Select, NumberInput, Modal, Text, Group, Card, Stack, Title, Loader, ActionIcon, Badge } from '@mantine/core';
import { IconEdit } from '@tabler/icons-react';
import gql from 'graphql-tag';
import { useState } from 'react';
import useTaxonomy from '../../../../hooks/useTaxonomy';

const GET_USERS_WITH_TIMEOFF_SETTINGS = gql`
  query GetUsersWithTimeOffSettings {
    admin {
      users(excludeInactive: true) {
        _id
        firstName
        lastName
        emails {
          address
        }
        timeOffSettings {
          timeOffType
          annualEarnedHours
          maxRolloverHours
        }
      }
    }
  }
`;

const UPDATE_USER_TIME_OFF_SETTINGS = gql`
  mutation UpdateUserTimeOffSettings($userId: ID!, $settings: [TimeOffSettingInput!]!) {
    updateUserTimeOffSettings(userId: $userId, settings: $settings) {
      _id
      timeOffSettings {
        timeOffType
        annualEarnedHours
        maxRolloverHours
      }
    }
  }
`;

interface TimeOffSetting {
  timeOffType: string;
  annualEarnedHours: number;
  maxRolloverHours: number | null;
}

interface EditingUser {
  _id: string;
  firstName: string;
  lastName: string;
  settings: TimeOffSetting[];
}

const UserTimeOffSettings = () => {
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<EditingUser | null>(null);
  const [editingSettings, setEditingSettings] = useState<TimeOffSetting[]>([]);

  const { data: timeOffTypesQuery } = useTaxonomy('Time Off');
  const timeOffTypes = timeOffTypesQuery?.taxonomy?.terms || [];

  const { data: usersData, loading: usersLoading } = useQuery(GET_USERS_WITH_TIMEOFF_SETTINGS);

  const [updateSettings, { loading: updateLoading }] = useMutation(UPDATE_USER_TIME_OFF_SETTINGS, {
    refetchQueries: [{ query: GET_USERS_WITH_TIMEOFF_SETTINGS }],
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      console.log('Settings saved successfully:', data);
      setEditModalOpen(false);
      setEditingUser(null);
    },
    onError: (error) => {
      console.error('Error updating settings:', error);
      alert('Error saving settings: ' + error.message);
    },
  });

  const users = usersData?.admin?.users || [];

  const handleEditUser = (user: any) => {
    setEditingUser({
      _id: user._id,
      firstName: user.firstName,
      lastName: user.lastName,
      settings: user.timeOffSettings || [],
    });
    
    // Initialize settings with existing values or empty for each time off type
    const existingSettings = user.timeOffSettings || [];
    const initialSettings = timeOffTypes.map((type: any) => {
      const existing = existingSettings.find((s: any) => s.timeOffType === type.name);
      return {
        timeOffType: type.name,
        annualEarnedHours: existing?.annualEarnedHours || 0,
        maxRolloverHours: existing?.maxRolloverHours ?? null,
      };
    });
    setEditingSettings(initialSettings);
    setEditModalOpen(true);
  };

  const handleSaveSettings = () => {
    if (!editingUser) return;
    
    // Filter out settings with 0 earned hours and null rollover (no config)
    const settingsToSave = editingSettings.filter(
      s => s.annualEarnedHours > 0 || s.maxRolloverHours !== null
    );

    console.log('Saving settings for user:', editingUser._id);
    console.log('Settings to save:', settingsToSave);

    updateSettings({
      variables: {
        userId: editingUser._id,
        settings: settingsToSave.map(s => ({
          timeOffType: s.timeOffType,
          annualEarnedHours: s.annualEarnedHours,
          maxRolloverHours: s.maxRolloverHours,
        })),
      },
    });
  };

  const updateSettingValue = (timeOffType: string, field: 'annualEarnedHours' | 'maxRolloverHours', value: number | null) => {
    setEditingSettings(prev => 
      prev.map(s => 
        s.timeOffType === timeOffType 
          ? { ...s, [field]: value }
          : s
      )
    );
  };

  // Filter users based on selection
  const filteredUsers = selectedUserId 
    ? users.filter((u: any) => u._id === selectedUserId)
    : users;

  // Users with settings configured
  const usersWithSettings = users.filter((u: any) => u.timeOffSettings?.length > 0);

  if (usersLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader size="lg" />
        <Text ml="md">Loading users...</Text>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      <Card shadow="sm" padding="lg" radius="md" withBorder style={{ overflow: 'visible' }}>
        <Stack>
          <Title order={3}>Employee Time Off Settings</Title>
          <Text color="dimmed" size="sm">
            Configure annual earned hours and maximum rollover hours for each employee.
            These settings determine how much time off is credited at the start of each year.
          </Text>

          <Group>
            <Select
              label="Filter by Employee"
              placeholder="All employees"
              clearable
              searchable
              withinPortal
              dropdownPosition="bottom"
              data={users.map((user: any) => ({
                value: user._id,
                label: `${user.firstName} ${user.lastName} (${user.emails?.[0]?.address || 'No email'})`,
              }))}
              value={selectedUserId}
              onChange={setSelectedUserId}
              style={{ width: 400 }}
            />
            <Badge size="lg" color="blue" style={{ marginTop: 24 }}>
              {usersWithSettings.length} / {users.length} employees configured
            </Badge>
          </Group>
        </Stack>
      </Card>

      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Table withBorder withColumnBorders striped highlightOnHover>
          <thead>
            <tr>
              <th>Employee</th>
              <th>Email</th>
              {timeOffTypes.map((type: any) => (
                <th key={type._id}>{type.name}</th>
              ))}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user: any) => (
              <tr key={user._id}>
                <td>{user.firstName} {user.lastName}</td>
                <td>{user.emails?.[0]?.address || '-'}</td>
                {timeOffTypes.map((type: any) => {
                  const setting = user.timeOffSettings?.find((s: any) => s.timeOffType === type.name);
                  return (
                    <td key={type._id}>
                      {setting ? (
                        <div>
                          <Text size="sm">Earned: {setting.annualEarnedHours} hrs</Text>
                          <Text size="xs" color="dimmed">
                            Max Rollover: {setting.maxRolloverHours ?? 'Default'}
                          </Text>
                        </div>
                      ) : (
                        <Text size="sm" color="dimmed">Not configured</Text>
                      )}
                    </td>
                  );
                })}
                <td>
                  <ActionIcon color="blue" onClick={() => handleEditUser(user)}>
                    <IconEdit size={18} />
                  </ActionIcon>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>

      {/* Edit Modal */}
      <Modal
        opened={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title={`Edit Time Off Settings - ${editingUser?.firstName} ${editingUser?.lastName}`}
        size="lg"
      >
        <Stack>
          <Text size="sm" color="dimmed">
            Configure annual earned hours and maximum rollover hours for each time off type.
            Leave max rollover blank to use the global default.
          </Text>

          {editingSettings.map((setting) => (
            <Card key={setting.timeOffType} withBorder padding="sm">
              <Text weight={500} mb="xs">{setting.timeOffType}</Text>
              <Group grow>
                <NumberInput
                  label="Annual Earned Hours"
                  description="Hours credited at start of year"
                  value={setting.annualEarnedHours}
                  onChange={(value) => updateSettingValue(setting.timeOffType, 'annualEarnedHours', value || 0)}
                  min={0}
                  step={8}
                />
                <NumberInput
                  label="Max Rollover Hours"
                  description="Leave blank for global default"
                  value={setting.maxRolloverHours ?? undefined}
                  onChange={(value) => updateSettingValue(setting.timeOffType, 'maxRolloverHours', typeof value === 'number' ? value : null)}
                  min={0}
                  step={8}
                  placeholder="Use default"
                />
              </Group>
            </Card>
          ))}

          <Group position="right" mt="md">
            <Button variant="outline" onClick={() => setEditModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveSettings} loading={updateLoading}>
              Save Settings
            </Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
};

export default UserTimeOffSettings;

